import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { handleAppDownload, openDemoBookingModal, openEliteBuyerModal } from '../utils/functions';
import './Header.css';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMobileToggle, setShowMobileToggle] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({
    product: false,
    event: false,
    demo: false
  });
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState({
    product: false,
    event: false,
    demo: false
  });
  const location = useLocation();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleDropdown = (menu) => {
    setDropdownOpen(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  const toggleDesktopDropdown = (menu) => {
    setDesktopDropdownOpen(prev => {
      const newState = {
        product: false,
        event: false,
        demo: false
      };
      newState[menu] = !prev[menu];
      return newState;
    });
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setDropdownOpen({
      product: false,
      event: false,
      demo: false
    });
  };

  const closeDesktopDropdowns = () => {
    setDesktopDropdownOpen({
      product: false,
      event: false,
      demo: false
    });
  };

  const handleSectionScroll = (e, path) => {
    const [route, hash] = path.split('#');
    
    // If we're already on the same route, just scroll to the section
    if (location.pathname === route && hash) {
      e.preventDefault();
      setTimeout(() => {
        const element = document.querySelector(`#${hash}`);
        if (element) {
          const headerOffset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
    // Otherwise, let React Router handle navigation and scroll will happen in useEffect
  };

  useEffect(() => {
    closeMobileMenu();
    closeDesktopDropdowns();
    
    // Scroll to section if hash is present in URL
    if (location.hash) {
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          const headerOffset = 100; // Account for fixed header
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on dropdown toggle, menu, or menu items
      const clickedDropdown = event.target.closest('.dropdown');
      const clickedDropdownToggle = event.target.closest('.dropdown-toggle-link, .btn-book-demo-nav');
      
      if (!clickedDropdown && !clickedDropdownToggle) {
        closeDesktopDropdowns();
      }
    };

    if (Object.values(desktopDropdownOpen).some(val => val)) {
      // Use capture phase to ensure we catch the click
      document.addEventListener('click', handleClickOutside, true);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [desktopDropdownOpen]);

  // Detect overlap with logo and adjust navbar positioning based on zoom
  useEffect(() => {
    let lastZoomLevel = window.devicePixelRatio || 1;
    
    const checkOverlap = () => {
      const navCenter = document.querySelector('.nav-center');
      const navMenu = document.querySelector('.nav-menu');
      const logo = document.querySelector('.logo');
      const mobileToggle = document.querySelector('.mobile-menu-toggle');
      const navActions = document.querySelector('.nav-actions');
      const navbar = document.querySelector('.navbar');
      const container = document.querySelector('.main-header .container');
      
      if (!navCenter || !navMenu || !logo || !navbar) return;

      // Get viewport width
      const viewportWidth = window.innerWidth;
      
      // Get element positions and dimensions
      const logoRect = logo.getBoundingClientRect();
      const navMenuRect = navMenu.getBoundingClientRect();
      const navCenterRect = navCenter.getBoundingClientRect();
      const navbarRect = navbar.getBoundingClientRect();
      const navActionsRect = navActions ? navActions.getBoundingClientRect() : null;
      
      // Calculate widths
      const logoWidth = logoRect.width;
      const navCenterWidth = navCenterRect.width;
      const navActionsWidth = navActionsRect ? navActionsRect.width : 0;
      const mobileToggleWidth = mobileToggle && mobileToggle.offsetParent ? mobileToggle.getBoundingClientRect().width : 0;
      
      // Calculate required spacing
      const logoRightEdge = logoRect.right;
      const minSpacingAfterLogo = 50; // Minimum space needed after logo
      const minSpacingBeforeActions = 20; // Minimum space before nav-actions
      const containerPadding = 40; // Container padding
      
      // Calculate total width needed
      const totalNeededWidth = logoWidth + minSpacingAfterLogo + navCenterWidth + minSpacingBeforeActions + navActionsWidth + containerPadding;
      
      // Check if nav menu overlaps with logo (with buffer)
      const navMenuLeftEdge = navMenuRect.left;
      const isOverlappingWithLogo = logoRightEdge > (navMenuLeftEdge - 10);
      
      // Check if nav-actions extends beyond viewport or overlaps with nav-center
      let isOverlappingWithActions = false;
      let isActionsCutOff = false;
      if (navActionsRect) {
        const navCenterRight = navCenterRect.right;
        const navActionsLeft = navActionsRect.left;
        const navActionsRight = navActionsRect.right;
        
        // Check if nav-center overlaps with nav-actions
        isOverlappingWithActions = navCenterRight > (navActionsLeft - 15);
        
        // Check if nav-actions is cut off at viewport edge
        isActionsCutOff = navActionsRight > (viewportWidth - 20);
      }
      
      // Check if total content exceeds available space
      const insufficientSpace = totalNeededWidth > viewportWidth;
      
      // Check if viewport is too small (lower threshold for better responsiveness)
      const viewportTooSmall = viewportWidth < 1200;
      
      // Also check if content would overflow even with current layout
      const buttonGroup = document.querySelector('.button-group');
      const buttonGroupWidth = buttonGroup ? buttonGroup.getBoundingClientRect().width : 0;
      const totalNavContentWidth = navCenterWidth + buttonGroupWidth + navActionsWidth;
      const availableNavbarWidth = navbarRect.width - logoWidth - 100; // Account for padding/margins
      const wouldOverflow = totalNavContentWidth > availableNavbarWidth;
      
      // Show mobile toggle if any overlap or overflow detected
      const shouldShowMobile = (
        isOverlappingWithLogo || 
        isOverlappingWithActions || 
        isActionsCutOff ||
        insufficientSpace || 
        wouldOverflow ||
        viewportTooSmall
      );
      
      setShowMobileToggle(shouldShowMobile);
      
      // Update display
      if (shouldShowMobile) {
        // Hide desktop nav and show mobile toggle
        if (navCenter) {
          navCenter.style.display = 'none';
          navCenter.style.marginLeft = '';
          navCenter.style.marginRight = '';
        }
        if (navActions) {
          navActions.style.display = 'none';
        }
        if (mobileToggle) {
          mobileToggle.style.display = 'flex';
          mobileToggle.classList.add('visible');
        }
      } else {
        // Show desktop nav and hide mobile toggle
        if (navCenter) {
          navCenter.style.display = '';
          // Reset any inline styles that might interfere
          navCenter.style.marginLeft = '';
        }
        if (navActions) {
          navActions.style.display = '';
        }
        if (mobileToggle) {
          mobileToggle.style.display = 'none';
          mobileToggle.classList.remove('visible');
        }
      }
    };

    // Initial check with delay to ensure DOM is ready
    const timeoutId = setTimeout(checkOverlap, 100);
    
    // Debounce function for performance
    let resizeTimeout;
    const debouncedCheck = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkOverlap, 50);
    };
    
    // Check on resize and zoom changes
    window.addEventListener('resize', debouncedCheck);
    window.addEventListener('orientationchange', checkOverlap);
    
    // Listen for zoom changes using multiple methods
    let lastZoom = window.devicePixelRatio;
    let lastInnerWidth = window.innerWidth;
    let lastOuterWidth = window.outerWidth;
    
    const checkZoom = () => {
      const currentZoom = window.devicePixelRatio;
      const currentInnerWidth = window.innerWidth;
      const currentOuterWidth = window.outerWidth;
      
      // Check for zoom changes (devicePixelRatio changes)
      const zoomChanged = Math.abs(currentZoom - lastZoom) > 0.05;
      
      // Check for browser zoom (innerWidth vs outerWidth ratio changes)
      const innerOuterRatio = currentInnerWidth / currentOuterWidth;
      const lastInnerOuterRatio = lastInnerWidth / lastOuterWidth;
      const browserZoomChanged = Math.abs(innerOuterRatio - lastInnerOuterRatio) > 0.01;
      
      if (zoomChanged || browserZoomChanged) {
        lastZoom = currentZoom;
        lastInnerWidth = currentInnerWidth;
        lastOuterWidth = currentOuterWidth;
        setTimeout(checkOverlap, 50);
      }
    };
    
    // Also listen for visual viewport changes (better for mobile zoom)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', debouncedCheck);
      window.visualViewport.addEventListener('scroll', debouncedCheck);
    }
    
    // Use ResizeObserver for element size changes
    // Wrap in requestAnimationFrame to avoid "ResizeObserver loop completed with undelivered notifications" error
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        debouncedCheck();
        checkZoom();
      });
    });
    
    const navbar = document.querySelector('.navbar');
    if (navbar) {
      resizeObserver.observe(navbar);
    }

    const container = document.querySelector('.container');
    if (container) {
      resizeObserver.observe(container);
    }
    
    // Also observe logo and nav-center for size changes
    const logoEl = document.querySelector('.logo');
    if (logoEl) {
      resizeObserver.observe(logoEl);
    }
    
    const navCenterEl = document.querySelector('.nav-center');
    if (navCenterEl) {
      resizeObserver.observe(navCenterEl);
    }
    
    // Periodic check for zoom changes (fallback)
    const zoomCheckInterval = setInterval(checkZoom, 500);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(resizeTimeout);
      clearInterval(zoomCheckInterval);
      window.removeEventListener('resize', debouncedCheck);
      window.removeEventListener('orientationchange', checkOverlap);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', debouncedCheck);
        window.visualViewport.removeEventListener('scroll', debouncedCheck);
      }
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <>
      <header className="main-header">
        <div className="container">
          <nav className="navbar">
            <div className="logo">
              <Link to="/">
                <img src="/assets/images/Logo_bsb_white.webp" alt="BSB Polymers Logo" className="logo-img" onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'inline';
                }} />
                <span className="logo-text" style={{ display: 'none' }}>BSB POLYMERS</span>
              </Link>
            </div>
            
            <button 
              className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''} ${showMobileToggle ? 'visible' : ''}`}
              aria-label="Toggle menu"
              onClick={toggleMobileMenu}
              style={{ display: showMobileToggle ? 'flex' : 'none' }}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            
            <div className="nav-center" style={{ display: showMobileToggle ? 'none' : 'flex' }}>
              <ul className="nav-menu">
                <li><Link to="/">Home</Link></li>
                <li><Link to="/about">About Us</Link></li>
                <li 
                  className={`dropdown ${desktopDropdownOpen.product ? 'active' : ''}`}
                >
                  <a 
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      toggleDesktopDropdown('product');
                    }}
                    className="dropdown-toggle-link"
                  >
                    Product <i className={`fas fa-chevron-${desktopDropdownOpen.product ? 'up' : 'down'} chevron`}></i>
                  </a>
                  <ul className={`dropdown-menu ${desktopDropdownOpen.product ? 'show' : ''}`}>
                    <li>
                      <Link 
                        to="/services#polymer" 
                        onClick={(e) => {
                          closeDesktopDropdowns();
                          handleSectionScroll(e, '/services#polymer');
                        }}
                      >
                        Polymer (Virgin)
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/services#logistics" 
                        onClick={(e) => {
                          closeDesktopDropdowns();
                          handleSectionScroll(e, '/services#logistics');
                        }}
                      >
                        Logistics
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/services#nbfc" 
                        onClick={(e) => {
                          closeDesktopDropdowns();
                          handleSectionScroll(e, '/services#nbfc');
                        }}
                      >
                        Trade Finance (NBFC)
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/services#recycle" 
                        onClick={(e) => {
                          closeDesktopDropdowns();
                          handleSectionScroll(e, '/services#recycle');
                        }}
                      >
                        Polymer (Recycle)
                      </Link>
                    </li>
                  </ul>
                </li>
                <li 
                  className={`dropdown ${desktopDropdownOpen.event ? 'active' : ''}`}
                >
                  <a 
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      toggleDesktopDropdown('event');
                    }}
                    className="dropdown-toggle-link"
                  >
                    Event <i className={`fas fa-chevron-${desktopDropdownOpen.event ? 'up' : 'down'} chevron`}></i>
                  </a>
                  <ul className={`dropdown-menu ${desktopDropdownOpen.event ? 'show' : ''}`}>
                    <li>
                      <Link 
                        to="/events#synergy-meet" 
                        onClick={(e) => {
                          closeDesktopDropdowns();
                          handleSectionScroll(e, '/events#synergy-meet');
                        }}
                      >
                        Synergy Meet West Zone
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/events#elite-buyer" 
                        onClick={(e) => {
                          closeDesktopDropdowns();
                          handleSectionScroll(e, '/events#elite-buyer');
                        }}
                      >
                        Elite Buyer Club
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/events#channel-partner" 
                        onClick={(e) => {
                          closeDesktopDropdowns();
                          handleSectionScroll(e, '/events#channel-partner');
                        }}
                      >
                        Channel Partner Meet
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/events#supplier-meet" 
                        onClick={(e) => {
                          closeDesktopDropdowns();
                          handleSectionScroll(e, '/events#supplier-meet');
                        }}
                      >
                        Supplier Meet
                      </Link>
                    </li>
                  </ul>
                </li>
                <li><Link to="/elite-buyer-club">Elite Buyer Club</Link></li>
              </ul>
              <div className="button-group">
                <a 
                  href="https://bsbfortradeprime.com/check_browser.php" 
                  className="nav-btn btn-download-app-nav" 
                  target="_blank" 
                  rel="noopener"
                >
                  <i className="fas fa-download"></i>
                  <span>Download App</span>
                </a>
                <div 
                  className={`dropdown book-demo-menu ${desktopDropdownOpen.demo ? 'active' : ''}`}
                >
                  <a 
                    href="#" 
                    className="nav-btn btn-book-demo-nav"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleDesktopDropdown('demo');
                    }}
                  >
                    <span>Book Demo</span>
                    <i className={`fas fa-chevron-${desktopDropdownOpen.demo ? 'up' : 'down'} chevron`}></i>
                  </a>
                  <ul className={`dropdown-menu demo-dropdown-simple ${desktopDropdownOpen.demo ? 'show' : ''}`}>
                    <li>
                      <a 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          openDemoBookingModal('polymer');
                          closeDesktopDropdowns();
                        }} 
                        className="demo-link"
                      >
                        <i className="fas fa-cubes"></i>
                        <span>Polymer Support</span>
                        <i className="fas fa-arrow-right"></i>
                      </a>
                    </li>
                    <li>
                      <a 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          openDemoBookingModal('logistics');
                          closeDesktopDropdowns();
                        }} 
                        className="demo-link"
                      >
                        <i className="fas fa-truck"></i>
                        <span>Logistics Support</span>
                        <i className="fas fa-arrow-right"></i>
                      </a>
                    </li>
                  </ul>
                </div>
                <a 
                  href="#" 
                  className="nav-btn btn-elite-nav" 
                  onClick={(e) => {
                    e.preventDefault();
                    openEliteBuyerModal();
                  }}
                >
                  <i className="fas fa-crown"></i>
                  <span>Elite Buyer</span>
                </a>
              </div>
            </div>
            <div className="nav-actions" style={{ display: showMobileToggle ? 'none' : 'flex' }}>
              <a href="tel:+919825555470" className="nav-btn btn-call-nav">
                <i className="fas fa-phone"></i>
                <span>Call</span>
              </a>
            </div>
          </nav>
        </div>
      </header>

      <div
        className={`mobile-nav-overlay ${mobileMenuOpen ? 'active' : ''}`}
        id="mobile-nav"
        onClick={closeMobileMenu}
        role="button"
        tabIndex={mobileMenuOpen ? 0 : -1}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') closeMobileMenu(); }}
        aria-label="Close menu"
      >
        <div className="mobile-nav-content" onClick={(e) => e.stopPropagation()}>
        <ul className="nav-menu">
          <li><Link to="/" onClick={closeMobileMenu}>Home</Link></li>
          <li><Link to="/about" onClick={closeMobileMenu}>About Us</Link></li>
          <li className={`dropdown mobile-dropdown ${dropdownOpen.product ? 'active' : ''}`}>
            <a 
              href="#" 
              className="dropdown-toggle"
              onClick={(e) => {
                e.preventDefault();
                toggleDropdown('product');
              }}
            >
              Product 
              <i className={`fas fa-chevron-${dropdownOpen.product ? 'up' : 'down'} chevron`}></i>
            </a>
            <ul className={`dropdown-menu ${dropdownOpen.product ? 'show' : ''}`}>
              <li>
                <Link 
                  to="/services#polymer" 
                  onClick={(e) => {
                    closeMobileMenu();
                    handleSectionScroll(e, '/services#polymer');
                  }}
                >
                  Polymer (Virgin)
                </Link>
              </li>
              <li>
                <Link 
                  to="/services#logistics" 
                  onClick={(e) => {
                    closeMobileMenu();
                    handleSectionScroll(e, '/services#logistics');
                  }}
                >
                  Logistics
                </Link>
              </li>
              <li>
                <Link 
                  to="/services#nbfc" 
                  onClick={(e) => {
                    closeMobileMenu();
                    handleSectionScroll(e, '/services#nbfc');
                  }}
                >
                  Trade Finance (NBFC)
                </Link>
              </li>
              <li>
                <Link 
                  to="/services#recycle" 
                  onClick={(e) => {
                    closeMobileMenu();
                    handleSectionScroll(e, '/services#recycle');
                  }}
                >
                  Polymer (Recycle)
                </Link>
              </li>
            </ul>
          </li>
          <li className={`dropdown mobile-dropdown ${dropdownOpen.event ? 'active' : ''}`}>
            <a 
              href="#" 
              className="dropdown-toggle"
              onClick={(e) => {
                e.preventDefault();
                toggleDropdown('event');
              }}
            >
              Event 
              <i className={`fas fa-chevron-${dropdownOpen.event ? 'up' : 'down'} chevron`}></i>
            </a>
            <ul className={`dropdown-menu ${dropdownOpen.event ? 'show' : ''}`}>
              <li>
                <Link 
                  to="/events#synergy-meet" 
                  onClick={(e) => {
                    closeMobileMenu();
                    handleSectionScroll(e, '/events#synergy-meet');
                  }}
                >
                  Synergy Meet West Zone
                </Link>
              </li>
              <li>
                <Link 
                  to="/events#elite-buyer" 
                  onClick={(e) => {
                    closeMobileMenu();
                    handleSectionScroll(e, '/events#elite-buyer');
                  }}
                >
                  Elite Buyer Club
                </Link>
              </li>
              <li>
                <Link 
                  to="/events#channel-partner" 
                  onClick={(e) => {
                    closeMobileMenu();
                    handleSectionScroll(e, '/events#channel-partner');
                  }}
                >
                  Channel Partner Meet
                </Link>
              </li>
              <li>
                <Link 
                  to="/events#supplier-meet" 
                  onClick={(e) => {
                    closeMobileMenu();
                    handleSectionScroll(e, '/events#supplier-meet');
                  }}
                >
                  Supplier Meet
                </Link>
              </li>
            </ul>
          </li>
          <li><Link to="/elite-buyer-club" onClick={closeMobileMenu}>Elite Buyer Club</Link></li>
        </ul>
        <div className="button-group">
          <a 
            href="https://bsbfortradeprime.com/check_browser.php" 
            className="nav-btn btn-download-app-nav" 
            target="_blank" 
            rel="noopener"
            onClick={closeMobileMenu}
          >
            <i className="fas fa-download"></i>
            <span>Download App</span>
          </a>
          <div className={`dropdown book-demo-menu mobile-dropdown ${dropdownOpen.demo ? 'active' : ''}`}>
            <a 
              href="#" 
              className="nav-btn btn-book-demo-nav dropdown-toggle"
              onClick={(e) => {
                e.preventDefault();
                toggleDropdown('demo');
              }}
            >
              <span>Book Demo</span>
              <i className={`fas fa-chevron-${dropdownOpen.demo ? 'up' : 'down'} chevron`}></i>
            </a>
            <ul className={`dropdown-menu demo-dropdown-simple ${dropdownOpen.demo ? 'show' : ''}`}>
              <li>
                <a 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    openDemoBookingModal('polymer');
                    closeMobileMenu();
                  }} 
                  className="demo-link"
                >
                  <i className="fas fa-cubes"></i>
                  <span>Polymer Support</span>
                  <i className="fas fa-arrow-right"></i>
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    openDemoBookingModal('logistics');
                    closeMobileMenu();
                  }} 
                  className="demo-link"
                >
                  <i className="fas fa-truck"></i>
                  <span>Logistics Support</span>
                  <i className="fas fa-arrow-right"></i>
                </a>
              </li>
            </ul>
          </div>
          <Link 
            to="/elite-buyer-club" 
            className="nav-btn btn-elite-nav"
            onClick={closeMobileMenu}
          >
            <i className="fas fa-crown"></i>
            <span>Elite Buyer</span>
          </Link>
        </div>
        <div className="nav-actions">
          <a 
            href="tel:+919825555470" 
            className="nav-btn btn-call-nav"
            onClick={closeMobileMenu}
          >
            <i className="fas fa-phone-alt"></i>
            <span>Call</span>
          </a>
        </div>
        </div>
      </div>
    </>
  );
};

export default Header;

